import React from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { I18n } from "react-redux-i18n";

import { Search } from "@common/components/Search";
import { getTestAttributes } from "@common/utils/tests";
import { Flex } from "@common/components/Flex";
import { MessageBox } from "@common/components/MessageBox";
import { FieldQuery } from "@common/components/field-query";
import { FieldQueryPredefinedControlType } from "@common/components/field-query/field-query.types";
import { WithUseDrop } from "@common/components/with-use-drop/with-use-drop";
import FormatManagement, {
  FORMAT_MANAGEMENT_MODE,
} from "@common/components/FormatManagement";
import { TreeGetIdArgs } from "@common/components/Tree";

import { Actions } from "./components/actions";
import { SpreadsheetManagementProps } from "./spreadsheet-management.types";
import { useSpreadsheetManagementController } from "./spreadsheet-management.controller";
import { Folders } from "./components/folders";
import { Fields } from "./components/fields";
import { SPREADSHEET_MODULE_NAME } from "../../constants/shared";
import { ConditionsBuilder } from "../conditions-builder";
import { SpreadsheetResult } from "@modules/spreadsheets/pages/spreadsheet-details/components/spreadsheet-result";

import "./spreadsheet-management.styles.scss";

export const SpreadsheetManagement: React.FC<SpreadsheetManagementProps> = (
  props
) => {
  const { spreadsheetType, onModeSwitch } = props;

  const {
    folders,
    isPedigreeFoldersLoading,
    isIndividualFoldersLoading,
    fields,
    isFieldsFromSearch,
    handleSelectedFolderChange,
    selectedFolder,
    isFieldsLoading,
    selectedFields,
    handleSelectedFolderFieldChange,
    handleFieldDoubleClick,
    handleQueryBuilderConditionsChange,
    handleAllQueryBuilderConditionsRemove,
    handleDropQueryBuilderConditions,
    messageProps,
    closeMessage,
    handleGetFields,
    queryBuilderConditions,
    itemUniqProperty,
    getId,
    handleColumnsTableFieldsRemove,
    setColumnsTableFields,
    handleColumnsTableFieldDrop,
    columnsTable,
    columnsTableFields,
    columnsSortTable,
    sortTableFields,
    currentSpreadsheetTypeConfig,
    setSortTableFields,
    handleSortTableFieldsRemove,
    formatManagementController,
    handleLoadFormat,
    handleSaveFormat,
    isAllowedSaveFormatSubmit,
    showResults,
    runPayload,
    handleRunSpreadsheet,
    setShowResults,
    isRunSpreadsheetLoading,
    spreadsheetId,
    handleRowDrop
  } = useSpreadsheetManagementController(props);

  const handleModeSwitchWithResultsState = React.useCallback(
    (mode: 'results' | 'management') => {
      if (mode === 'management') {
        setShowResults(false);
      } else if (mode === 'results') {
        setShowResults(true);
      }

      if (onModeSwitch) {
        onModeSwitch(mode);
      }
    },
    [onModeSwitch, setShowResults]
  );

  // Modified run handler that switches to results mode
  const handleRunWithModeSwitch = React.useCallback(async () => {
    try {      
      await handleRunSpreadsheet();
      // After successful run, switch to results mode
    } catch (error) {
      // Error handling is done in handleRunSpreadsheet
      console.error('Failed to run spreadsheet:', error);
    }
  }, [handleRunSpreadsheet, onModeSwitch]);

  // If showing results, render the SpreadsheetResult component
  // Always use runPayload when running from builder to reflect current builder state (columns, query, sorting)
  // Use spreadsheetId from controller state (which updates when format is loaded) instead of props
  if (showResults && runPayload) {
    return (
      <div className="SpreadsheetManagement">
        <SpreadsheetResult
          spreadsheetType={props.spreadsheetType}
          runPayload={runPayload}
          isTemporaryRun={true}
          spreadsheetId={spreadsheetId}
          onModeSwitch={handleModeSwitchWithResultsState}
          initialFolderId={props.initialFolderId}
          initialFamilyId={props.initialFamilyId}
        />
      </div>
    );
  }
  return (
    <div className="SpreadsheetDetailsSpreadsheetManagement">
      <MessageBox hideMessageBox={closeMessage} {...messageProps} />
      <Actions
        spreadsheetType={spreadsheetType}
        disabledState={{
          run: false,
          saveFmt: !columnsTableFields.length,
        }}
        onLoadFormat={formatManagementController.openLoadFormat}
        onSaveFmt={formatManagementController.openSaveFormat}
        onRun={handleRunWithModeSwitch}
        isRunLoading={isRunSpreadsheetLoading}
      />
      {formatManagementController.loadFormatOpened && (
        <FormatManagement
          showFormatNameHeader={false}
          title={currentSpreadsheetTypeConfig.loadFmtTitle}
          getId={{
            modalTitle: "fp-datasheet-format-folder-picker-panel",
            creatingFolderName: "tx-datasheet-add-folder-wnd-folder-name",
            creatingFolderCancelBtn: "lbl-datasheet-add-folder-wnd-cancel",
            creatingFolderCreateBtn: "lbl-datasheet-add-folder-wnd-save",
            field: ({ rowIndex }: { rowIndex: number; colIndex: number }) =>
              `bsgrid-${rowIndex}-0`,
            creatingFolderItem: ({
              parentIndex,
              ownIndex,
              data,
            }: TreeGetIdArgs) =>
              parentIndex === undefined
                ? `folder-${ownIndex}`
                : `folder-child-${data.id}`,
            creatingFolderCollapseItem: ({
              parentIndex,
              ownIndex,
            }: TreeGetIdArgs) =>
              parentIndex === undefined
                ? `folder-${ownIndex}-fld-icon`
                : `folder-child-${ownIndex}-fld-icon`,
            folder: ({ parentIndex, ownIndex, data }: TreeGetIdArgs) =>
              parentIndex === undefined
                ? `folder-${ownIndex}`
                : `folder-child-${data.id}`,
            collapseIcon: ({ parentIndex, ownIndex }: TreeGetIdArgs) =>
              parentIndex === undefined
                ? `folder-${ownIndex}-fld-icon`
                : `folder-child-${ownIndex}-fld-icon`,
            newFolderBtn: "btn-datasheet-format-folder-picker-new-folder",
            deleteFolderBtn: "btn-datasheet-format-folder-picker-delete-folder",
            deleteFormatBtn: "btn-datasheet-format-folder-picker-delete-format",
            cancelBtn: "btn-datasheet-format-folder-picker-cancel",
            submitBtn: "btn-datasheet-format-folder-picker-ok",
          }}
          onFormatDelete={formatManagementController.handleFormatDelete}
          onFolderCreate={formatManagementController.handleFolderCreate}
          onFolderDelete={formatManagementController.handleFolderDelete}
          resetFormats={formatManagementController.handleFormatsReset}
          folders={formatManagementController.folders}
          formats={formatManagementController.formats}
          getFolders={formatManagementController.getAndSetFolders}
          getFormats={formatManagementController.getAndSetFormats}
          close={formatManagementController.closeLoadFormat}
          onSubmit={handleLoadFormat}
        />
      )}
      {formatManagementController.saveFormatOpened && (
        <FormatManagement
          formatNameInputLabel={I18n.t("Name")}
          showDescriptionInput={true}
          showFormatNameHeader={false}
          mode={FORMAT_MANAGEMENT_MODE.save}
          title={currentSpreadsheetTypeConfig.saveFmtTitle}
          initialFormatName={
            formatManagementController.currentFormat?.name || ""
          }
          initialFormatDescription={
            formatManagementController.currentFormat?.description || ""
          }
          getId={{
            modalTitle: "fp-datasheet-format-folder-picker-panel",
            creatingFolderName: "tx-datasheet-add-folder-wnd-folder-name",
            creatingFolderCancelBtn: "lbl-datasheet-add-folder-wnd-cancel",
            creatingFolderCreateBtn: "lbl-datasheet-add-folder-wnd-save",
            field: ({ rowIndex }: { rowIndex: number; colIndex: number }) =>
              `bsgrid-${rowIndex}-0`,
            creatingFolderItem: ({
              parentIndex,
              ownIndex,
              data,
            }: TreeGetIdArgs) =>
              parentIndex === undefined
                ? `folder-${ownIndex}`
                : `folder-child-${data.id}`,
            creatingFolderCollapseItem: ({
              parentIndex,
              ownIndex,
            }: TreeGetIdArgs) =>
              parentIndex === undefined
                ? `folder-${ownIndex}-fld-icon`
                : `folder-child-${ownIndex}-fld-icon`,
            folder: ({ parentIndex, ownIndex, data }: TreeGetIdArgs) =>
              parentIndex === undefined
                ? `folder-${ownIndex}`
                : `folder-child-${data.id}`,
            collapseIcon: ({ parentIndex, ownIndex }: TreeGetIdArgs) =>
              parentIndex === undefined
                ? `folder-${ownIndex}-fld-icon`
                : `folder-child-${ownIndex}-fld-icon`,
            newFolderBtn: "btn-datasheet-format-folder-picker-new-folder",
            deleteFolderBtn: "btn-datasheet-format-folder-picker-delete-folder",
            deleteFormatBtn: "btn-datasheet-format-folder-picker-delete-format",
            cancelBtn: "btn-datasheet-format-folder-picker-cancel",
            submitBtn: "btn-datasheet-format-folder-picker-ok",
          }}
          onFormatDelete={formatManagementController.handleFormatDelete}
          onFolderCreate={formatManagementController.handleFolderCreate}
          onFolderDelete={formatManagementController.handleFolderDelete}
          resetFormats={formatManagementController.handleFormatsReset}
          folders={formatManagementController.folders}
          formats={formatManagementController.formats}
          getFolders={formatManagementController.getAndSetFolders}
          getFormats={formatManagementController.getAndSetFormats}
          close={formatManagementController.closeSaveFormat}
          onSubmit={handleSaveFormat}
          isAllowedToSubmit={isAllowedSaveFormatSubmit}
        />
      )}
      <div className="SpreadsheetDetailsSpreadsheetManagement__content">
        <Search
          {...getTestAttributes({
            id: `${currentSpreadsheetTypeConfig.idPrefix}-builder-whs-searchwnd-suggest-box`,
          })}
          withLiveSearch
          className="SpreadsheetDetailsSpreadsheetManagement__content__search"
          classes={{
            searchForm:
              "SpreadsheetDetailsSpreadsheetManagement__content__search__form",
          }}
          onSearch={(value: { searchKeyword: string }) => {
            handleGetFields({ searchQuery: value.searchKeyword });
          }}
        />
        <Flex className="SpreadsheetDetailsSpreadsheetManagement__content__management">
          <Folders
            {...getTestAttributes(
              SPREADSHEET_MODULE_NAME,
              "spreadsheet-management",
              "folder-selector"
            )}
            selectedFolder={selectedFolder}
            folders={folders}
            onSelectedFolderChange={handleSelectedFolderChange}
            loading={isIndividualFoldersLoading || isPedigreeFoldersLoading}
            className="SpreadsheetDetailsSpreadsheetManagement__content__management__folders"
            getId={getId.folders}
          />
          <DndProvider
            backend={HTML5Backend}
            options={{ enableTouchEvents: false, enableMouseEvents: true }}
          >
            <Flex width="100%">
              <Fields
                {...getTestAttributes({
                  id: `${currentSpreadsheetTypeConfig.idPrefix}-builder-whs-fild-list`,
                })}
                fields={fields}
                isFieldsFromSearch={isFieldsFromSearch}
                loading={isFieldsLoading}
                className="SpreadsheetDetailsSpreadsheetManagement__content__management__folder-fields"
                itemUniqProperty={itemUniqProperty}
                selection={{
                  items: selectedFields,
                  onChange: handleSelectedFolderFieldChange,
                  type: "multiple",
                }}
                getId={getId.fields}
                onDoubleClick={handleFieldDoubleClick}
              />
              <div className="SpreadsheetDetailsSpreadsheetManagement__content__management__builder">
                <h6 className="SpreadsheetDetailsSpreadsheetManagement__content__management__builder__title">
                  {I18n.t("Columns")}
                </h6>
                <WithUseDrop
                  onDrop={handleColumnsTableFieldDrop}
                  name="field"
                  className="SpreadsheetDetailsSpreadsheetManagement__content__management__builder__column"
                >
                  <FieldQuery
                    columns={columnsTable}
                    placeholder={I18n.t(
                      "Drag Fields From Warehouse And Drop Here"
                    )}
                    className="SpreadsheetDetailsSpreadsheetManagement__content__management__builder__column__form"
                    items={columnsTableFields}
                    getId={getId.builderFieldsPan}
                    controls={[
                      {
                        type: FieldQueryPredefinedControlType.moveUp,
                        handler: setColumnsTableFields,
                      },
                      {
                        type: FieldQueryPredefinedControlType.moveDown,
                        handler: setColumnsTableFields,
                      },
                      {
                        type: FieldQueryPredefinedControlType.removeAll,
                        handler: handleColumnsTableFieldsRemove,
                      },
                    ]}
                    classes={{
                      row: "SpreadsheetDetailsSpreadsheetManagement__content__management__builder__column__row",
                    }}
                    onRowDrop={handleRowDrop}
                    enableDragDrop={true}
                  />
                </WithUseDrop>
                <h6 className="SpreadsheetDetailsSpreadsheetManagement__content__management__builder__title">
                  {I18n.t("Sort (Optional)")}
                </h6>
                <FieldQuery
                  columns={columnsSortTable}
                  placeholder={I18n.t("Move Fields From Columns Panel Above")}
                  className="SpreadsheetDetailsSpreadsheetManagement__content__management__builder__column"
                  items={sortTableFields}
                  getId={getId.builderSortPan}
                  classes={{
                    row: "SpreadsheetDetailsSpreadsheetManagement__content__management__builder__column__row",
                  }}
                  controls={[
                    {
                      type: FieldQueryPredefinedControlType.moveUp,
                      handler: setSortTableFields,
                    },
                    {
                      type: FieldQueryPredefinedControlType.moveDown,
                      handler: setSortTableFields,
                    },
                    {
                      type: FieldQueryPredefinedControlType.removeAll,
                      handler: handleSortTableFieldsRemove,
                    },
                  ]}
                />
                <h6 className="SpreadsheetDetailsSpreadsheetManagement__content__management__builder__title">
                  {I18n.t("Query (Optional)")}
                </h6>
                <WithUseDrop
                  onDrop={handleDropQueryBuilderConditions}
                  name="field"
                  className="SpreadsheetDetailsSpreadsheetManagement__content__management__builder__column"
                >
                  <ConditionsBuilder
                    conditions={queryBuilderConditions}
                    moduleName={SPREADSHEET_MODULE_NAME}
                    className="SpreadsheetDetailsSpreadsheetManagement__content__management__builder__column__form"
                    onChange={handleQueryBuilderConditionsChange}
                    onAllConditionsRemove={
                      handleAllQueryBuilderConditionsRemove
                    }
                    onDrop={handleDropQueryBuilderConditions}
                  />
                </WithUseDrop>
              </div>
            </Flex>
          </DndProvider>
        </Flex>
      </div>
    </div>
  );
};
